import {defaultPropertyDefinitions} from "@buildwithflux/constants";
import {generatePresetCreationDataFor, getProperty, type INetsMap} from "@buildwithflux/core";
import {IPropertyData, SubjectPropertyUpdateMap, type NetId, type NetType} from "@buildwithflux/models";

/**
 * Builds a PropertyUpdateMap for updating net type properties based on classified types.
 * Only updates nets that don't have user-set net type values.
 *
 * @param netsMap - Map of all nets in the document
 * @param netTypes - Map of net IDs to their classified types
 * @returns PropertyUpdateMap for Redux update action
 */
export function buildNetPropertyUpdates(netsMap: INetsMap, netTypes: Record<NetId, NetType>): SubjectPropertyUpdateMap {
    const propertyUpdatesMap: SubjectPropertyUpdateMap = {};

    for (const [netUid, netType] of Object.entries(netTypes)) {
        const net = netsMap[netUid];
        if (!net) {
            continue;
        }

        // Ideally, there should always be a net type property since we're ensuring it exists on document load
        const existingNetTypeProperty = getProperty(defaultPropertyDefinitions.net_type.label, net.properties);

        // If the net type is already set by user, we don't override it
        if (existingNetTypeProperty && !existingNetTypeProperty.isSetBySystem && !!existingNetTypeProperty.value) {
            continue;
        }

        let updatedNetTypeProperty: IPropertyData;
        if (existingNetTypeProperty) {
            // If the property already exists, we update it
            updatedNetTypeProperty = {
                ...existingNetTypeProperty,
                value: netType,
            };
        } else {
            // If the property doesn't exist, we create a new one
            updatedNetTypeProperty = generatePresetCreationDataFor("net_type").create({
                order: Object.keys(net.properties).length,
                value: netType,
            });
        }

        propertyUpdatesMap[netUid] = {
            subjectType: "nets",
            propertyUpdates: {
                [updatedNetTypeProperty.uid]: updatedNetTypeProperty,
            },
        };
    }

    return propertyUpdatesMap;
}
